import { apiService } from '@/services/apiService'
import { connectivityService } from '@/services/connectivityService'
import { parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { useOfflineStore } from '@/stores/offlineStore'

type Resolution = '50m' | '5m'
type ParsedGeometryMap = Map<string, [number, number][][]>

function parseGeometryBundle(entries: Record<string, Array<string | null>>): ParsedGeometryMap {
  const result: ParsedGeometryMap = new Map()
  Object.entries(entries).forEach(([id, tracksGeometry]) => {
    result.set(
      id,
      tracksGeometry.map((lineString) => (lineString ? parseLineString(lineString) : [])),
    )
  })
  return result
}

export async function loadWorkspaceGeometries(
  workspaceIds: string[],
  resolution: Resolution,
  signal?: AbortSignal,
): Promise<ParsedGeometryMap> {
  const offlineStore = useOfflineStore()
  const result: ParsedGeometryMap = new Map()
  const missing: string[] = []

  for (const workspaceId of workspaceIds) {
    const snapshot = await offlineStore.readWorkspaceSnapshot(workspaceId)
    const bundle = snapshot?.geometryByResolution?.[resolution]
    if (bundle?.workspaceTracksGeometry?.length) {
      result.set(
        workspaceId,
        bundle.workspaceTracksGeometry.map((lineString) =>
          lineString ? parseLineString(lineString) : [],
        ),
      )
      continue
    }

    missing.push(workspaceId)
  }

  if (missing.length === 0 || !connectivityService.isEffectivelyOnline.value) {
    return result
  }

  try {
    const response = await apiService.getWorkspaceTracksGeometry(missing, resolution, signal)
    response.forEach((item) => {
      result.set(
        item.clientId,
        item.tracksGeometry.map((lineString) => (lineString ? parseLineString(lineString) : [])),
      )
    })
  } catch (error) {
    if ((error as Error).name !== 'CanceledError') {
      logService.warn(LOG_CATEGORIES.MAP, 'Failed to load workspace geometry from provider', {
        resolution,
        error,
      })
    }
  }

  return result
}

export async function loadTourGeometries(
  tourIds: string[],
  resolution: Resolution,
  signal?: AbortSignal,
): Promise<ParsedGeometryMap> {
  const offlineStore = useOfflineStore()
  const result: ParsedGeometryMap = new Map()
  const missing = new Set(tourIds)

  const workspaceIds = Object.values(offlineStore.index)
    .filter((entry) => entry.packageKind === 'workspace')
    .map((entry) => entry.workspaceId)
  for (const workspaceId of workspaceIds) {
    const snapshot = await offlineStore.readWorkspaceSnapshot(workspaceId)
    const bundle = snapshot?.geometryByResolution?.[resolution]?.tourTracksGeometry
    if (!bundle) continue

    const parsed = parseGeometryBundle(bundle)
    parsed.forEach((coords, tourId) => {
      if (!missing.has(tourId)) return
      result.set(tourId, coords)
      missing.delete(tourId)
    })
  }

  if (missing.size === 0 || !connectivityService.isEffectivelyOnline.value) {
    return result
  }

  try {
    const response = await apiService.getTourTracksGeometry(Array.from(missing), resolution, signal)
    response.forEach((item) => {
      result.set(
        item.clientId,
        item.tracksGeometry.map((lineString) => (lineString ? parseLineString(lineString) : [])),
      )
    })
  } catch (error) {
    if ((error as Error).name !== 'CanceledError') {
      logService.warn(LOG_CATEGORIES.MAP, 'Failed to load tour geometry from provider', {
        resolution,
        error,
      })
    }
  }

  return result
}
